/**
 * Theme Panel - Color & Typography Editor
 *
 * Side panel for editing template theme colors and fonts.
 * Changes propagate to the template JSON in real-time.
 * Uses plain-language labels so designers know what each color affects.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Palette, Type, ChevronDown, ChevronRight, X, Check, Sparkles, AlertTriangle } from 'lucide-react';

// ── Contrast ratio utilities (WCAG 2.1) ──
function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const h = hex.replace('#', '');
  if (h.length !== 6 && h.length !== 3) return null;
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const num = parseInt(full, 16);
  if (isNaN(num)) return null;
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function relativeLuminance({ r, g, b }) {
  const [rs, gs, bs] = [r, g, b].map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function contrastRatio(hex1, hex2) {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return null;
  const l1 = relativeLuminance(rgb1);
  const l2 = relativeLuminance(rgb2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function getContrastLevel(ratio) {
  if (ratio === null) return null;
  if (ratio >= 7) return 'AAA';
  if (ratio >= 4.5) return 'AA';
  if (ratio >= 3) return 'AA Large';
  return 'Fail';
}

// ── Pre-made Fashion Color Palettes ──────────────────────────────────
const FASHION_PALETTES = [
  {
    name: 'Bridal Gold',
    description: 'Rich & Traditional',
    colors: { primary: '#B8860B', secondary: '#8B0000', accent: '#DAA520', background: '#FFFEF7', surface: '#FFF8E7', text: '#2C1810', textSecondary: '#6B5444', border: '#E8DCC8' },
  },
  {
    name: 'Modern Minimal',
    description: 'Clean & Contemporary',
    colors: { primary: '#1A1A1A', secondary: '#333333', accent: '#666666', background: '#FFFFFF', surface: '#F5F5F5', text: '#1A1A1A', textSecondary: '#6B7280', border: '#E5E5E5' },
  },
  {
    name: 'Rose Garden',
    description: 'Feminine & Elegant',
    colors: { primary: '#BE185D', secondary: '#9D174D', accent: '#EC4899', background: '#FFFFFF', surface: '#FDF2F8', text: '#1F2937', textSecondary: '#6B7280', border: '#FECDD3' },
  },
  {
    name: 'Royal Indigo',
    description: 'Premium & Luxurious',
    colors: { primary: '#4338CA', secondary: '#3730A3', accent: '#6366F1', background: '#FFFFFF', surface: '#EEF2FF', text: '#1E1B4B', textSecondary: '#6B7280', border: '#C7D2FE' },
  },
  {
    name: 'Earth & Handloom',
    description: 'Artisan & Heritage',
    colors: { primary: '#92400E', secondary: '#78350F', accent: '#D97706', background: '#FFFBEB', surface: '#FEF3C7', text: '#451A03', textSecondary: '#78716C', border: '#E7D5B0' },
  },
  {
    name: 'Forest Green',
    description: 'Nature & Sustainable',
    colors: { primary: '#166534', secondary: '#14532D', accent: '#22C55E', background: '#FFFFFF', surface: '#F0FDF4', text: '#1A1A1A', textSecondary: '#6B7280', border: '#BBF7D0' },
  },
  {
    name: 'Ocean Teal',
    description: 'Fresh & Calming',
    colors: { primary: '#0D9488', secondary: '#115E59', accent: '#14B8A6', background: '#FFFFFF', surface: '#F0FDFA', text: '#1A1A1A', textSecondary: '#6B7280', border: '#CCFBF1' },
  },
  {
    name: 'Festive Saffron',
    description: 'Vibrant & Celebratory',
    colors: { primary: '#EA580C', secondary: '#C2410C', accent: '#F97316', background: '#FFFFFF', surface: '#FFF7ED', text: '#1A1A1A', textSecondary: '#6B7280', border: '#FDBA74' },
  },
  {
    name: 'Midnight Luxe',
    description: 'Bold & Dramatic',
    colors: { primary: '#D4AF37', secondary: '#1A1A2E', accent: '#C9A84C', background: '#0F0F1A', surface: '#1A1A2E', text: '#F5F5F5', textSecondary: '#A0A0B0', border: '#2A2A3E' },
  },
  {
    name: 'Pastel Dream',
    description: 'Soft & Romantic',
    colors: { primary: '#A78BFA', secondary: '#7C3AED', accent: '#C4B5FD', background: '#FFFFFF', surface: '#F5F3FF', text: '#1F2937', textSecondary: '#6B7280', border: '#DDD6FE' },
  },
];

const FONT_OPTIONS = [
  'Inter', 'Playfair Display', 'Montserrat', 'Roboto', 'Open Sans',
  'Lato', 'Poppins', 'Raleway', 'Oswald', 'Merriweather',
  'Nunito', 'Source Sans Pro', 'PT Sans', 'Noto Sans', 'Ubuntu',
  'Cormorant Garamond', 'Lora', 'Crimson Text', 'DM Sans', 'Work Sans',
  'Libre Baskerville', 'Josefin Sans', 'Quicksand', 'Abril Fatface', 'Cinzel'
];

// Font pairing suggestions: heading font → recommended body fonts
const FONT_PAIRINGS = {
  'Playfair Display': ['Inter', 'Lato', 'Open Sans', 'Montserrat'],
  'Cormorant Garamond': ['Montserrat', 'Raleway', 'Open Sans', 'Lato'],
  'Poppins': ['Inter', 'Lato', 'DM Sans', 'Open Sans'],
  'Montserrat': ['Lora', 'Merriweather', 'Open Sans', 'Roboto'],
  'Lora': ['Montserrat', 'Poppins', 'Inter', 'Raleway'],
  'Inter': ['Playfair Display', 'Lora', 'Crimson Text'],
  'DM Sans': ['Playfair Display', 'Lora', 'Cormorant Garamond'],
  'Raleway': ['Merriweather', 'Lora', 'Crimson Text'],
  'Josefin Sans': ['Lato', 'Open Sans', 'Merriweather', 'Libre Baskerville'],
  'Roboto': ['Playfair Display', 'Lora', 'Libre Baskerville'],
};

// Plain language descriptions for each color role
const COLOR_DESCRIPTIONS = {
  primary: { label: 'Buttons & Headers', hint: 'Main brand color used for buttons, navigation bar, and dark section backgrounds' },
  secondary: { label: 'Secondary Accent', hint: 'Complementary color for badges, tags, and secondary buttons' },
  accent: { label: 'Highlights & Links', hint: 'Eye-catching color for links, icons, and special callouts' },
  background: { label: 'Page Background', hint: 'The main background color of your entire website' },
  text: { label: 'Body Text', hint: 'Color of paragraphs and general text content' },
  textSecondary: { label: 'Subtle Text', hint: 'Lighter text used for descriptions, captions, and labels' },
  textLight: { label: 'Muted Text', hint: 'Very light text for footnotes and placeholders' },
  surface: { label: 'Card Background', hint: 'Background color for cards, panels, and content boxes' },
  border: { label: 'Borders & Lines', hint: 'Color of dividers, card borders, and input outlines' },
};

const ColorInput = ({ label, hint, value, onChange, colorKey, onHover, onLeave }) => {
  const [showHint, setShowHint] = useState(false);

  return (
    <div
      className="py-2 px-1 rounded-lg hover:bg-gray-50 transition-colors duration-150"
      onMouseEnter={() => { setShowHint(true); onHover?.(colorKey); }}
      onMouseLeave={() => { setShowHint(false); onLeave?.(); }}
    >
      <div className="flex items-center gap-3">
        <div className="relative">
          <input
            type="color"
            value={value || '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="w-9 h-9 rounded-lg border-2 border-gray-200 cursor-pointer p-0.5 hover:border-blue-400 transition-colors"
          />
        </div>
        <div className="flex-1 min-w-0">
          <label className="text-xs font-medium text-gray-700 block">{label}</label>
          {showHint && hint && (
            <p className="text-[10px] text-gray-400 mt-0.5 leading-tight animate-fadeIn">{hint}</p>
          )}
        </div>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-20 text-xs px-2 py-1.5 border border-gray-200 rounded-md font-mono text-gray-600 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none transition-all"
          placeholder="#000000"
        />
      </div>
    </div>
  );
};

const FontSelect = ({ label, hint, value, onChange }) => {
  useEffect(() => {
    const families = FONT_OPTIONS.map(f => f.replace(/\s+/g, '+')).join('&family=');
    const id = 'theme-panel-font-preview';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${families}:wght@400;700&display=swap`;
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div className="py-2 px-1 rounded-lg hover:bg-gray-50 transition-colors duration-150">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
          <Type className="w-4 h-4 text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <label className="text-xs font-medium text-gray-700 block mb-1">{label}</label>
          {hint && <p className="text-[10px] text-gray-400 leading-tight">{hint}</p>}
        </div>
      </div>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-2 text-xs px-2.5 py-2 border border-gray-200 rounded-md bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none transition-all"
        style={{ fontFamily: value || 'inherit' }}
      >
        <option value="">Default</option>
        {FONT_OPTIONS.map(font => (
          <option key={font} value={font} style={{ fontFamily: font }}>{font}</option>
        ))}
      </select>
      {value && (
        <div className="mt-2 px-3 py-2 bg-gray-50 rounded-md">
          <p className="text-sm" style={{ fontFamily: value }}>
            The quick brown fox jumps over the lazy dog
          </p>
        </div>
      )}
    </div>
  );
};

const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, badge }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <Icon className="w-4 h-4 text-gray-400" />
        <span className="flex-1 text-left">{title}</span>
        {badge && (
          <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-500 rounded-full font-medium">{badge}</span>
        )}
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-3 pb-3">{children}</div>
      </div>
    </div>
  );
};

const ThemePanel = ({ theme, onChange, onClose }) => {
  const colors = theme?.colors || {};
  const typography = theme?.typography || {};
  const [showApplied, setShowApplied] = useState(false);
  const [highlightedColor, setHighlightedColor] = useState(null);

  const flashApplied = useCallback(() => {
    setShowApplied(true);
    setTimeout(() => setShowApplied(false), 1200);
  }, []);

  // Broadcast highlighted color role to the editor for visual feedback
  useEffect(() => {
    if (highlightedColor) {
      document.querySelector('.editable-renderer')?.setAttribute('data-highlight-color', highlightedColor);
    } else {
      document.querySelector('.editable-renderer')?.removeAttribute('data-highlight-color');
    }
  }, [highlightedColor]);

  const updateColor = useCallback((key, value) => {
    const newColors = { ...colors };

    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      if (typeof newColors[parent] === 'object' && newColors[parent] !== null && !Array.isArray(newColors[parent])) {
        newColors[parent] = { ...newColors[parent], [child]: value };
      } else {
        newColors[parent] = value;
      }
    } else {
      if (key === 'primary' && Array.isArray(newColors.primary)) {
        newColors.primary = [value, value];
      } else {
        newColors[key] = value;
      }
    }

    onChange({ ...theme, colors: newColors });
    flashApplied();
  }, [colors, theme, onChange, flashApplied]);

  const updateFont = useCallback((key, value) => {
    onChange({
      ...theme,
      typography: { ...typography, [key]: value }
    });
    flashApplied();
  }, [typography, theme, onChange, flashApplied]);

  // Extract flat color values
  const primaryColor = Array.isArray(colors.primary) ? colors.primary[0] : colors.primary;
  const textColor = typeof colors.text === 'object' ? colors.text.primary : colors.text;
  const textSecondary = typeof colors.text === 'object'
    ? colors.text.secondary
    : (colors.textSecondary || undefined);
  const textLight = typeof colors.text === 'object'
    ? colors.text.light
    : (colors.textLight || undefined);
  const bgColor = typeof colors.background === 'object' ? colors.background.primary : colors.background;
  const surfaceColor = typeof colors.surface === 'object' ? colors.surface.primary : colors.surface;
  const borderColor = typeof colors.border === 'object' ? colors.border.primary : colors.border;

  const accentColor = colors.accent || (() => {
    const palettes = ['amber', 'rose', 'orange', 'emerald', 'teal', 'blue', 'indigo', 'violet'];
    for (const pal of palettes) {
      if (colors[pal] && typeof colors[pal] === 'object') {
        return colors[pal]['900'] || colors[pal]['800'] || colors[pal]['700'];
      }
    }
    return undefined;
  })();

  const handleTextChange = useCallback((v) => {
    if (typeof colors.text === 'object') {
      updateColor('text.primary', v);
    } else {
      updateColor('text', v);
    }
  }, [colors.text, updateColor]);

  const handleTextSecondaryChange = useCallback((v) => {
    if (typeof colors.text === 'object') {
      updateColor('text.secondary', v);
    } else {
      updateColor('textSecondary', v);
    }
  }, [colors.text, updateColor]);

  const handleTextLightChange = useCallback((v) => {
    if (typeof colors.text === 'object') {
      updateColor('text.light', v);
    } else {
      updateColor('textLight', v);
    }
  }, [colors.text, updateColor]);

  const handleAccentChange = useCallback((v) => {
    updateColor('accent', v);
  }, [updateColor]);

  // Count active colors for badge
  const colorCount = [primaryColor, colors.secondary, accentColor, bgColor, textColor, textSecondary, textLight, surfaceColor, borderColor]
    .filter(Boolean).length;

  // Contrast ratio checks for accessibility warnings
  const contrastWarnings = useMemo(() => {
    const warnings = [];
    const textBgRatio = contrastRatio(textColor, bgColor);
    if (textBgRatio !== null && textBgRatio < 4.5) {
      warnings.push({
        label: 'Body Text on Background',
        ratio: textBgRatio.toFixed(1),
        level: getContrastLevel(textBgRatio),
      });
    }
    const textSurfaceRatio = contrastRatio(textColor, surfaceColor);
    if (textSurfaceRatio !== null && textSurfaceRatio < 4.5) {
      warnings.push({
        label: 'Body Text on Card',
        ratio: textSurfaceRatio.toFixed(1),
        level: getContrastLevel(textSurfaceRatio),
      });
    }
    if (primaryColor) {
      // Check white text on primary (buttons)
      const btnRatio = contrastRatio('#FFFFFF', primaryColor);
      if (btnRatio !== null && btnRatio < 3) {
        warnings.push({
          label: 'White text on Buttons',
          ratio: btnRatio.toFixed(1),
          level: getContrastLevel(btnRatio),
        });
      }
    }
    return warnings;
  }, [textColor, bgColor, surfaceColor, primaryColor]);

  return (
    <div className="w-80 bg-white border-l border-gray-200 h-full overflow-y-auto">
      {/* Highlight CSS injection */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-2px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.15s ease-out; }
        .editable-renderer[data-highlight-color="primary"] [class*="bg-gray-900"],
        .editable-renderer[data-highlight-color="primary"] [class*="bg-gray-950"],
        .editable-renderer[data-highlight-color="primary"] [class*="bg-black"],
        .editable-renderer[data-highlight-color="primary"] button[class*="bg-"] {
          outline: 3px solid #3b82f6 !important; outline-offset: 2px; transition: outline 0.2s;
        }
        .editable-renderer[data-highlight-color="background"] {
          outline: 3px solid #3b82f6 !important; outline-offset: -3px; transition: outline 0.2s;
        }
        .editable-renderer[data-highlight-color="text"] p,
        .editable-renderer[data-highlight-color="text"] span:not([class*="bg-"]),
        .editable-renderer[data-highlight-color="text"] h1,
        .editable-renderer[data-highlight-color="text"] h2,
        .editable-renderer[data-highlight-color="text"] h3 {
          outline: 2px solid #3b82f680 !important; outline-offset: 1px; transition: outline 0.2s;
        }
        .editable-renderer[data-highlight-color="surface"] [class*="bg-white"],
        .editable-renderer[data-highlight-color="surface"] [class*="rounded"] {
          outline: 3px solid #3b82f6 !important; outline-offset: 2px; transition: outline 0.2s;
        }
        .editable-renderer[data-highlight-color="border"] [class*="border-"] {
          border-color: #3b82f6 !important; border-width: 2px !important; transition: border 0.2s;
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          <h3 className="text-sm font-bold text-gray-800">Theme Designer</h3>
          {showApplied && (
            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full animate-fadeIn">
              <Check className="w-3 h-3" /> Applied
            </span>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1.5 hover:bg-white/80 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>

      {/* Quick Palettes */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Quick Palettes &mdash; Pick a Style
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
          {FASHION_PALETTES.map(palette => (
            <button
              key={palette.name}
              onClick={() => {
                const newColors = { ...colors };
                Object.entries(palette.colors).forEach(([key, value]) => {
                  if (key === 'text' && typeof newColors.text === 'object') {
                    newColors.text = { ...newColors.text, primary: value };
                  } else if (key === 'textSecondary' && typeof newColors.text === 'object') {
                    newColors.text = { ...newColors.text, secondary: value };
                  } else if (key === 'background' && typeof newColors.background === 'object') {
                    newColors.background = { ...newColors.background, primary: value };
                  } else if (key === 'surface' && typeof newColors.surface === 'object') {
                    newColors.surface = { ...newColors.surface, primary: value };
                  } else if (key === 'border' && typeof newColors.border === 'object') {
                    newColors.border = { ...newColors.border, primary: value };
                  } else if (key === 'primary' && Array.isArray(newColors.primary)) {
                    newColors.primary = [value, value];
                  } else {
                    newColors[key] = value;
                  }
                });
                onChange({ ...theme, colors: newColors });
                flashApplied();
              }}
              style={{
                padding: '8px', borderRadius: '10px', border: '1px solid #e2e8f0',
                backgroundColor: 'white', cursor: 'pointer', textAlign: 'left',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = palette.colors.primary; e.currentTarget.style.boxShadow = `0 2px 8px ${palette.colors.primary}20`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              {/* Color swatches */}
              <div style={{ display: 'flex', gap: '3px', marginBottom: '6px' }}>
                {[palette.colors.primary, palette.colors.secondary, palette.colors.accent, palette.colors.background, palette.colors.surface].map((c, i) => (
                  <div key={i} style={{ width: '16px', height: '16px', borderRadius: '4px', backgroundColor: c, border: '1px solid rgba(0,0,0,0.1)' }} />
                ))}
              </div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#1f2937' }}>{palette.name}</div>
              <div style={{ fontSize: '10px', color: '#9ca3af' }}>{palette.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Colors */}
      <CollapsibleSection title="Brand Colors" icon={Palette} badge={`${colorCount} active`}>
        <ColorInput
          label={COLOR_DESCRIPTIONS.primary.label}
          hint={COLOR_DESCRIPTIONS.primary.hint}
          value={primaryColor}
          colorKey="primary"
          onChange={(v) => updateColor('primary', v)}
          onHover={setHighlightedColor}
          onLeave={() => setHighlightedColor(null)}
        />
        {colors.secondary !== undefined && (
          <ColorInput
            label={COLOR_DESCRIPTIONS.secondary.label}
            hint={COLOR_DESCRIPTIONS.secondary.hint}
            value={colors.secondary}
            colorKey="secondary"
            onChange={(v) => updateColor('secondary', v)}
            onHover={setHighlightedColor}
            onLeave={() => setHighlightedColor(null)}
          />
        )}
        {accentColor !== undefined && (
          <ColorInput
            label={COLOR_DESCRIPTIONS.accent.label}
            hint={COLOR_DESCRIPTIONS.accent.hint}
            value={accentColor}
            colorKey="accent"
            onChange={handleAccentChange}
            onHover={setHighlightedColor}
            onLeave={() => setHighlightedColor(null)}
          />
        )}
        <ColorInput
          label={COLOR_DESCRIPTIONS.background.label}
          hint={COLOR_DESCRIPTIONS.background.hint}
          value={bgColor}
          colorKey="background"
          onChange={(v) => updateColor('background', v)}
          onHover={setHighlightedColor}
          onLeave={() => setHighlightedColor(null)}
        />
        <ColorInput
          label={COLOR_DESCRIPTIONS.text.label}
          hint={COLOR_DESCRIPTIONS.text.hint}
          value={textColor}
          colorKey="text"
          onChange={handleTextChange}
          onHover={setHighlightedColor}
          onLeave={() => setHighlightedColor(null)}
        />
        {textSecondary !== undefined && (
          <ColorInput
            label={COLOR_DESCRIPTIONS.textSecondary.label}
            hint={COLOR_DESCRIPTIONS.textSecondary.hint}
            value={textSecondary}
            colorKey="textSecondary"
            onChange={handleTextSecondaryChange}
            onHover={setHighlightedColor}
            onLeave={() => setHighlightedColor(null)}
          />
        )}
        {textLight !== undefined && (
          <ColorInput
            label={COLOR_DESCRIPTIONS.textLight.label}
            hint={COLOR_DESCRIPTIONS.textLight.hint}
            value={textLight}
            colorKey="textLight"
            onChange={handleTextLightChange}
            onHover={setHighlightedColor}
            onLeave={() => setHighlightedColor(null)}
          />
        )}
        {surfaceColor !== undefined && (
          <ColorInput
            label={COLOR_DESCRIPTIONS.surface.label}
            hint={COLOR_DESCRIPTIONS.surface.hint}
            value={surfaceColor}
            colorKey="surface"
            onChange={(v) => updateColor('surface', v)}
            onHover={setHighlightedColor}
            onLeave={() => setHighlightedColor(null)}
          />
        )}
        {borderColor !== undefined && (
          <ColorInput
            label={COLOR_DESCRIPTIONS.border.label}
            hint={COLOR_DESCRIPTIONS.border.hint}
            value={borderColor}
            colorKey="border"
            onChange={(v) => updateColor('border', v)}
            onHover={setHighlightedColor}
            onLeave={() => setHighlightedColor(null)}
          />
        )}

        {/* Contrast accessibility warnings */}
        {contrastWarnings.length > 0 && (
          <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-1.5 mb-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-[11px] font-semibold text-amber-700">Low Contrast</span>
            </div>
            {contrastWarnings.map((w, i) => (
              <div key={i} className="flex items-center justify-between text-[10px] py-0.5">
                <span className="text-amber-700">{w.label}</span>
                <span className={`font-mono font-semibold ${w.level === 'Fail' ? 'text-red-500' : 'text-amber-600'}`}>
                  {w.ratio}:1 ({w.level})
                </span>
              </div>
            ))}
            <p className="text-[9px] text-amber-500 mt-1">WCAG AA requires 4.5:1 for normal text</p>
          </div>
        )}
      </CollapsibleSection>

      {/* Typography */}
      <CollapsibleSection title="Fonts & Typography" icon={Type}>
        <FontSelect
          label="Heading Font"
          hint="Used for all titles, section headings, and hero text"
          value={typography.heading}
          onChange={(v) => updateFont('heading', v)}
        />
        <div className="my-2 border-t border-gray-100" />
        <FontSelect
          label="Body Font"
          hint="Used for paragraphs, descriptions, buttons, and labels"
          value={typography.body}
          onChange={(v) => updateFont('body', v)}
        />
        {/* Font pairing suggestions */}
        {typography.heading && FONT_PAIRINGS[typography.heading] && (
          <div style={{ padding: '8px 4px' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>
              Suggested body fonts for {typography.heading}:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {FONT_PAIRINGS[typography.heading].map(font => (
                <button
                  key={font}
                  onClick={() => updateFont('body', font)}
                  style={{
                    padding: '3px 10px', borderRadius: '6px', border: '1px solid #e2e8f0',
                    backgroundColor: typography.body === font ? '#dbeafe' : 'white',
                    fontSize: '11px', cursor: 'pointer', fontFamily: font,
                    color: typography.body === font ? '#1d4ed8' : '#4b5563',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {font}
                </button>
              ))}
            </div>
          </div>
        )}
      </CollapsibleSection>
    </div>
  );
};

export default ThemePanel;
